/**
 * Shared cart section refresh (page + drawer).
 * Cart Ajax + Section Rendering API.
 */
(function (window) {
  'use strict';

  var DEBOUNCE_MS = 400;

  function getSectionsUrl() {
    var path = window.location.pathname || '/';
    var search = window.location.search || '';
    return path + search;
  }

  function sectionsRequestUrl(sectionId) {
    var url = getSectionsUrl();
    var join = url.indexOf('?') >= 0 ? '&' : '?';
    return url + join + 'sections=' + encodeURIComponent(sectionId);
  }

  function collectSectionIds() {
    var ids = [];
    document.querySelectorAll('[data-ts-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-section-id') || (el.id || '').replace('shopify-section-', '');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function attachSections(payload) {
    payload = payload || {};
    var ids = collectSectionIds();
    if (!ids.length) return payload;
    payload.sections = ids;
    payload.sections_url = getSectionsUrl();
    return payload;
  }

  function syncHeaderCount(count) {
    document
      .querySelectorAll('[data-cart-count], .js-cart-count, .cart-count, .jsccount')
      .forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('hidden', count === 0);
      });
  }

  function CartSection(sectionEl, options) {
    options = options || {};
    this.sectionEl = sectionEl;
    this.sectionId = sectionEl.dataset.sectionId || sectionEl.getAttribute('data-section-id');
    this.root = sectionEl.querySelector('[data-ts-cart]');
    this.form = sectionEl.querySelector('[data-cart-form]');
    this.source = options.source || 'cart-section';
    this.enableNote = options.enableNote === true;
    this.enableExpand = options.enableExpand !== false;
    this.noteTimer = null;
    this.pending = false;

    if (!this.sectionId) {
      var wrap = sectionEl.closest('[id^="shopify-section-"]');
      if (wrap) this.sectionId = wrap.id.replace('shopify-section-', '');
    }

    if (!this.root) return;

    this.bindEvents();
    this.bindExternalUpdates();
    this.bindCheckoutFix();
  }

  CartSection.prototype.setLoading = function (loading) {
    var wrap =
      this.sectionEl.closest('.section-cart-wrap, .section-cart-drawer-wrap') || this.sectionEl;
    wrap.classList.toggle('is-loading', loading);
  };

  CartSection.prototype.bindEvents = function () {
    var self = this;
    if (this.sectionEl.dataset.cartEventsBound === 'true') return;
    this.sectionEl.dataset.cartEventsBound = 'true';

    this.sectionEl.addEventListener('click', function (event) {
      if (!self.root || !self.root.contains(event.target)) return;

      var minus = event.target.closest('[data-cart-qty-minus]');
      var plus = event.target.closest('[data-cart-qty-plus]');
      var remove = event.target.closest('[data-cart-remove]');
      var expand = event.target.closest('[data-cart-expand]');
      var discountApply = event.target.closest('[data-cart-discount-apply]');
      var discountRemove = event.target.closest('[data-cart-discount-remove]');

      if (minus) {
        event.preventDefault();
        self.adjustQty(minus.closest('[data-cart-qty]'), -1);
        return;
      }

      if (plus) {
        event.preventDefault();
        self.adjustQty(plus.closest('[data-cart-qty]'), 1);
        return;
      }

      if (remove) {
        event.preventDefault();
        self.changeLine(remove.dataset.lineKey, 0);
        return;
      }

      if (discountApply) {
        event.preventDefault();
        self.applyDiscountFromInput(discountApply.closest('[data-cart-discount]'));
        return;
      }

      if (discountRemove) {
        event.preventDefault();
        self.removeDiscountCode(discountRemove.dataset.discountCode);
        return;
      }

      if (expand && self.enableExpand) {
        event.preventDefault();
        self.toggleExpand(expand);
      }
    });

    this.sectionEl.addEventListener('keydown', function (event) {
      if (!self.root || !self.root.contains(event.target)) return;
      if (event.key !== 'Enter') return;

      var discountInput = event.target.closest('[data-cart-discount-input]');
      if (!discountInput) return;

      event.preventDefault();
      self.applyDiscountFromInput(discountInput.closest('[data-cart-discount]'));
    });

    this.sectionEl.addEventListener('change', function (event) {
      if (!self.root || !self.root.contains(event.target)) return;

      var input = event.target.closest('[data-cart-qty-input]');
      if (input) {
        self.onQtyInput(input);
        return;
      }

      if (!self.enableNote) return;
      var note = event.target.closest('[data-cart-note]');
      if (note) self.onNoteChange(note);
    });

    this.sectionEl.addEventListener('focusin', function (event) {
      if (!self.root || !self.root.contains(event.target)) return;
      var input = event.target.closest('[data-cart-qty-input]');
      if (input) input.dataset.prevValue = input.value;
    });
  };

  CartSection.prototype.bindExternalUpdates = function () {
    var self = this;
    if (this.sectionEl.dataset.cartExternalBound === 'true') return;
    this.sectionEl.dataset.cartExternalBound = 'true';

    document.addEventListener('ts:cart:updated', function (event) {
      var detail = event.detail || {};
      if (detail.source === self.source) return;
      self.refreshFromExternal(detail);
    });
  };

  CartSection.prototype.applySectionHtml = function (html, options) {
    if (!html) {
      window.location.reload();
      return false;
    }
    this.replaceSection(html, options);
    this.syncHeaderCountFromDom();
    return true;
  };

  CartSection.prototype.refreshFromExternal = function (detail) {
    var self = this;
    if (this.pending) return;

    var html = detail && detail.sections && this.sectionId && detail.sections[this.sectionId];
    if (html) {
      this.applySectionHtml(html, { broadcast: false });
      return;
    }

    this.pending = true;
    this.setLoading(true);

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then(function (cart) {
        return self.refreshSection(cart);
      })
      .catch(function () {
        window.location.reload();
      })
      .finally(function () {
        self.pending = false;
        self.setLoading(false);
      });
  };

  CartSection.prototype.adjustQty = function (wrap, delta) {
    if (!wrap) return;
    var input = wrap.querySelector('[data-cart-qty-input]');
    if (!input) return;

    var min = parseInt(input.min, 10) || 1;
    var max = parseInt(input.max, 10) || 9999;
    var next = (parseInt(input.value, 10) || min) + delta;
    next = Math.max(min, Math.min(max, next));

    if (String(next) === input.value) return;

    input.value = next;
    this.changeLine(input.dataset.lineKey, next);
  };

  CartSection.prototype.onQtyInput = function (input) {
    var min = parseInt(input.min, 10) || 1;
    var max = parseInt(input.max, 10) || 9999;
    var qty = parseInt(input.value, 10);

    if (isNaN(qty) || qty < min) {
      input.value = input.dataset.prevValue || min;
      return;
    }

    if (qty > max) {
      input.value = max;
      qty = max;
    }

    if (String(qty) === (input.dataset.prevValue || '')) return;

    this.changeLine(input.dataset.lineKey, qty);
  };

  CartSection.prototype.onNoteChange = function (textarea) {
    var self = this;
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(function () {
      self.updateNote(textarea.value);
    }, DEBOUNCE_MS);
  };

  CartSection.prototype.changeLine = function (lineKey, quantity) {
    var self = this;
    if (this.pending) return;
    this.pending = true;
    this.setLoading(true);

    var sectionIds = collectSectionIds();
    if (!sectionIds.length && this.sectionId) sectionIds = [this.sectionId];

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity,
        sections: sectionIds,
        sections_url: getSectionsUrl()
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart change failed');
        return res.json();
      })
      .then(function (cart) {
        var html = cart.sections && cart.sections[self.sectionId];
        if (!self.applySectionHtml(html, { skipUpsell: quantity > 0 })) return;
        if (typeof cart.item_count === 'number') syncHeaderCount(cart.item_count);
      })
      .catch(function () {
        window.location.reload();
      })
      .finally(function () {
        self.pending = false;
        self.setLoading(false);
      });
  };

  CartSection.prototype.updateNote = function (note) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ note: note })
    }).catch(function () {
      /* silent */
    });
  };

  CartSection.prototype.getDiscountWrap = function () {
    return this.root && this.root.querySelector('[data-cart-discount]');
  };

  CartSection.prototype.parseDiscountCodes = function (value) {
    return String(value || '')
      .split(',')
      .map(function (code) {
        return code.trim();
      })
      .filter(Boolean);
  };

  CartSection.prototype.normalizeDiscountCode = function (code) {
    return String(code || '').trim();
  };

  CartSection.prototype.getAppliedDiscountCodes = function () {
    var wrap = this.getDiscountWrap();
    if (!wrap) return [];
    return this.parseDiscountCodes(wrap.getAttribute('data-discount-codes'));
  };

  CartSection.prototype.showDiscountError = function (message) {
    var wrap = this.getDiscountWrap();
    if (!wrap) return;
    var errorEl = wrap.querySelector('[data-cart-discount-error]');
    if (!errorEl) return;
    errorEl.textContent = message || wrap.getAttribute('data-discount-error-msg') || '';
    errorEl.hidden = !errorEl.textContent;
  };

  CartSection.prototype.clearDiscountError = function () {
    var wrap = this.getDiscountWrap();
    if (!wrap) return;
    var errorEl = wrap.querySelector('[data-cart-discount-error]');
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  };

  CartSection.prototype.codeWasApplied = function (cart, code) {
    var target = String(code || '').toLowerCase();
    if (!target || !cart) return false;

    var list = cart.discount_codes || [];
    for (var i = 0; i < list.length; i++) {
      var entry = list[i];
      var entryCode = String((entry && (entry.code || entry)) || '').toLowerCase();
      if (entryCode !== target) continue;
      if (typeof entry.applicable === 'boolean') return entry.applicable;
      return true;
    }

    var apps = cart.cart_level_discount_applications || [];
    for (var j = 0; j < apps.length; j++) {
      var title = String((apps[j] && apps[j].title) || '').toLowerCase();
      if (title === target) return true;
    }

    return false;
  };

  CartSection.prototype.applyDiscountFromInput = function (wrap) {
    wrap = wrap || this.getDiscountWrap();
    if (!wrap) return;

    var input = wrap.querySelector('[data-cart-discount-input]');
    var code = this.normalizeDiscountCode(input && input.value);
    if (!code) {
      this.showDiscountError();
      if (input) input.focus();
      return;
    }

    var existing = this.getAppliedDiscountCodes();
    var already = existing.some(function (item) {
      return item.toLowerCase() === code.toLowerCase();
    });
    if (already) {
      if (input) input.value = '';
      this.clearDiscountError();
      return;
    }

    this.updateDiscountCodes(existing.concat([code]), { attemptedCode: code, clearInput: true });
  };

  CartSection.prototype.removeDiscountCode = function (code) {
    code = this.normalizeDiscountCode(code);
    if (!code) return;

    var next = this.getAppliedDiscountCodes().filter(function (item) {
      return item.toLowerCase() !== code.toLowerCase();
    });

    this.updateDiscountCodes(next);
  };

  CartSection.prototype.updateDiscountCodes = function (codes, options) {
    var self = this;
    options = options || {};
    if (this.pending) return;

    var discountValue = (codes || []).join(',');
    var attemptedCode = this.normalizeDiscountCode(options.attemptedCode);
    var sectionIds = collectSectionIds();
    if (!sectionIds.length && this.sectionId) sectionIds = [this.sectionId];

    this.pending = true;
    this.setLoading(true);
    this.clearDiscountError();

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        discount: discountValue,
        sections: sectionIds,
        sections_url: getSectionsUrl()
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Discount update failed');
        return res.json();
      })
      .then(function (cart) {
        if (attemptedCode && !self.codeWasApplied(cart, attemptedCode)) {
          var previous = (codes || []).filter(function (item) {
            return item.toLowerCase() !== attemptedCode.toLowerCase();
          });

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              discount: previous.join(','),
              sections: sectionIds,
              sections_url: getSectionsUrl()
            })
          })
            .then(function (res) {
              if (!res.ok) throw new Error('Discount revert failed');
              return res.json();
            })
            .then(function (reverted) {
              var html = reverted.sections && reverted.sections[self.sectionId];
              if (html) self.applySectionHtml(html);
              self.showDiscountError();
              var wrap = self.getDiscountWrap();
              var input = wrap && wrap.querySelector('[data-cart-discount-input]');
              if (input) {
                input.value = attemptedCode;
                input.focus();
              }
            });
        }

        var html = cart.sections && cart.sections[self.sectionId];
        if (!self.applySectionHtml(html)) return;
        if (typeof cart.item_count === 'number') syncHeaderCount(cart.item_count);

        if (options.clearInput) {
          var nextWrap = self.getDiscountWrap();
          var nextInput = nextWrap && nextWrap.querySelector('[data-cart-discount-input]');
          if (nextInput) nextInput.value = '';
        }
      })
      .catch(function () {
        self.showDiscountError();
      })
      .finally(function () {
        self.pending = false;
        self.setLoading(false);
      });
  };

  CartSection.prototype.refreshSection = function (cart) {
    var self = this;
    var url = sectionsRequestUrl(this.sectionId);

    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var html = data.sections && data.sections[self.sectionId];
        if (!self.applySectionHtml(html)) return;
        if (cart && typeof cart.item_count === 'number') syncHeaderCount(cart.item_count);
      });
  };

  CartSection.prototype.replaceSection = function (html, options) {
    options = options || {};
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var incoming =
      doc.getElementById('shopify-section-' + this.sectionId) ||
      doc.querySelector('[data-ts-cart-section][data-section-id="' + this.sectionId + '"]') ||
      doc.querySelector('[data-ts-cart]');

    if (!incoming) {
      window.location.reload();
      return;
    }

    var next = incoming.querySelector('[data-ts-cart]') || (incoming.matches('[data-ts-cart]') ? incoming : null);
    var current = this.sectionEl.querySelector('[data-ts-cart]');
    if (!next || !current) {
      window.location.reload();
      return;
    }

    current.replaceWith(next);
    this.root = next;
    this.form = this.sectionEl.querySelector('[data-cart-form]');
    this.bindCheckoutFix();

    if (options.broadcast === false) return;

    document.dispatchEvent(
      new CustomEvent('ts:cart:updated', {
        bubbles: true,
        detail: {
          sectionId: this.sectionId,
          source: this.source,
          skipUpsell: !!options.skipUpsell
        }
      })
    );
  };

  CartSection.prototype.syncHeaderCountFromDom = function () {
    var root = this.root || this.sectionEl.querySelector('[data-ts-cart]');
    if (!root) return;
    var count = parseInt(root.getAttribute('data-item-count'), 10);
    if (!isNaN(count)) syncHeaderCount(count);
  };

  CartSection.prototype.toggleExpand = function (button) {
    var list = this.root.querySelector('[data-cart-line-items]');
    if (!list) return;

    var expanded = list.classList.toggle('is-expanded');
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    var label = button.querySelector('[data-cart-expand-label]');
    if (label) label.textContent = expanded ? 'Collapse' : 'Expand';

    list.querySelectorAll('.ts-cart-item--collapsed').forEach(function (item) {
      if (expanded) item.removeAttribute('hidden');
      else item.setAttribute('hidden', '');
    });
  };

  CartSection.prototype.getCheckoutDiscountCodes = function (cartData) {
    var codes = [];
    var seen = {};
    var list = (cartData && cartData.discount_codes) || [];

    for (var i = 0; i < list.length; i++) {
      var entry = list[i];
      if (!entry) continue;
      var code = this.normalizeDiscountCode(entry.code || entry);
      if (!code) continue;
      if (typeof entry.applicable === 'boolean' && !entry.applicable) continue;
      var key = code.toLowerCase();
      if (seen[key]) continue;
      seen[key] = true;
      codes.push(code);
    }

    if (codes.length) return codes;
    return this.getAppliedDiscountCodes();
  };

  CartSection.prototype.bindCheckoutFix = function () {
    var self = this;
    var cartForm = this.form;
    if (!cartForm || cartForm.dataset.checkoutBound === 'true') return;
    cartForm.dataset.checkoutBound = 'true';

    var checkoutBtns = cartForm.querySelectorAll('[data-cart-checkout], [name="checkout"]');

    checkoutBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();

        fetch('/cart.js', { headers: { 'Cache-Control': 'no-cache' }, credentials: 'same-origin' })
          .then(function (response) {
            return response.json();
          })
          .then(function (cartData) {
            if (cartData.item_count === 0) {
              window.location.reload();
              return;
            }

            var codes = self.getCheckoutDiscountCodes(cartData);
            if (codes.length) {
              var root =
                (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
              window.location.href =
                root + 'checkout?discount=' + encodeURIComponent(codes.join(','));
              return;
            }

            cartForm.setAttribute('action', '/cart?t=' + Date.now());

            var hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.name = 'checkout';
            hiddenInput.value = 'Checkout';
            cartForm.appendChild(hiddenInput);

            cartForm.submit();
          })
          .catch(function () {
            cartForm.submit();
          });
      });
    });
  };

  window.tsCart = {
    collectSectionIds: collectSectionIds,
    attachSections: attachSections,
    syncHeaderCount: syncHeaderCount,
    CartSection: CartSection
  };
})(window);
